import DataTable from "react-data-table-component";
import { Smartphone, Globe, ExternalLink, Search, Edit2, Trash2, CircleCheck, XCircle, Clock, LayoutGrid, CheckSquare } from "lucide-react";
import { useState } from "react";
import { motion } from "framer-motion";
import { useAuth } from "../../Auth/AuthContext";

export default function AppsWebTable({ data, onEdit, onDelete, isLoading }) {
  const [searchText, setSearchText] = useState("");
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";
  const handleSearchChange = (e) => setSearchText(e.target.value.toLowerCase());

  const stats = [
    {
      name: "Total Properties",
      value: data.length,
      icon: LayoutGrid,
      color: "text-indigo-600",
      bg: "bg-indigo-100",
    },
    {
      name: "Apps",
      value: data.filter((d) => d.type === "App").length,
      icon: Smartphone,
      color: "text-purple-600",
      bg: "bg-purple-100",
    },
    {
      name: "Websites",
      value: data.filter((d) => d.type === "Website").length,
      icon: Globe,
      color: "text-emerald-600",
      bg: "bg-emerald-100",
    },
    {
      name: "Approved",
      value: data.filter((d) => d.status === "Approved").length,
      icon: CheckSquare,
      color: "text-green-600",
      bg: "bg-green-100",
    },
  ];

  const columns = [
    {
      name: "Publisher",
      selector: (row) => row.publisherName,
      cell: (row) => (
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-white text-xs font-semibold flex-shrink-0">
            {row.publisherName?.charAt(0)?.toUpperCase() || "?"}
          </div>
          <span className="text-sm text-gray-800">{row.publisherName || <span className="text-gray-400 italic">—</span>}</span>
        </div>
      ),
      sortable: true,
      minWidth: "160px",
    },
    {
      name: "Name",
      selector: (row) => row.name,
      cell: (row) => <span className="text-sm font-medium text-gray-800">{row.name}</span>,
      sortable: true,
      minWidth: "140px",
    },
    {
      name: "Type",
      cell: (row) => (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold ${
          row.type === "App" ? "bg-purple-100 text-purple-700" : "bg-emerald-100 text-emerald-700"
        }`}>
          {row.type === "App" ? <Smartphone className="w-3 h-3" /> : <Globe className="w-3 h-3" />}
          {row.type}
        </span>
      ),
      minWidth: "70px",
    },
    {
      name: "Platform",
      selector: (row) => row.platform,
      cell: (row) => (
        <span className="text-sm text-gray-600">{row.platform || <span className="text-gray-400 italic">—</span>}</span>
      ),
      minWidth: "70px",
    },
    {
      name: "Details",
      grow: 2,
      cell: (row) => (
        <div className="text-xs text-gray-600 space-y-0.5">
          {row.type === "App" ? (
            <>
              <div className="flex items-center gap-1">
                <span className="font-medium text-gray-500">Link:</span>
                {row.appLink ? (
                  <a href={row.appLink} target="_blank" rel="noopener noreferrer"
                    className="text-indigo-600 inline-flex items-center gap-1 hover:underline">
                    View <ExternalLink className="w-3 h-3" />
                  </a>
                ) : <span className="text-gray-400">—</span>}
              </div>
              <div className="flex items-center gap-1">
                <span className="font-medium text-gray-500">ID:</span>
                <span className="font-mono">{row.appId || <span className="text-gray-400">—</span>}</span>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-1">
              <span className="font-medium text-gray-500">URL:</span>
              {row.websiteLink ? (
                <a href={row.websiteLink} target="_blank" rel="noopener noreferrer"
                  className="text-indigo-600 inline-flex items-center gap-1 hover:underline">
                  Visit <ExternalLink className="w-3 h-3" />
                </a>
              ) : <span className="text-gray-400">—</span>}
            </div>
          )}
        </div>
      ),
    },
    {
      name: "Status",
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => {
        const map = {
          Approved: { cls: "bg-green-100 text-green-700", icon: <CircleCheck className="w-3.5 h-3.5" /> },
          Rejected: { cls: "bg-red-100 text-red-600", icon: <XCircle className="w-3.5 h-3.5" /> },
          "On approval": { cls: "bg-yellow-100 text-yellow-700", icon: <Clock className="w-3.5 h-3.5" /> },
        };
        const s = map[row.status] || { cls: "bg-gray-100 text-gray-600", icon: null };
        return (
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${s.cls}`}>
            {s.icon}{row.status}
          </span>
        );
      },
      minWidth: "130px",
    },
    ...(isAdmin
      ? [{
          name: "Actions",
          cell: (row) => (
            <div className="flex items-center gap-1">
              <button
                onClick={() => onEdit(row)}
                className="p-2 text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                title="Edit"
              >
                <Edit2 className="w-4 h-4" />
              </button>
              <button
                onClick={() => onDelete(row)}
                className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                title="Delete"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ),
          minWidth: "110px",
        }]
      : []),
  ];

  const filteredData = (data || []).filter((item) =>
    item.name?.toLowerCase().includes(searchText) ||
    item.type?.toLowerCase().includes(searchText) ||
    item.publisherName?.toLowerCase().includes(searchText) ||
    item.platform?.toLowerCase().includes(searchText) ||
    item.status?.toLowerCase().includes(searchText)
  );

  const customStyles = {
    table: { style: { backgroundColor: "transparent" } },
    rows: {
      style: {
        minHeight: "64px",
        backgroundColor: "transparent",
        borderBottom: "1px solid rgba(229, 231, 235, 0.5)",
        "&:hover": { backgroundColor: "rgba(99, 102, 241, 0.04)" },
      },
    },
    headCells: {
      style: {
        fontWeight: "600",
        backgroundColor: "rgba(249, 250, 251, 0.8)",
        fontSize: "0.75rem",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "#6b7280",
      },
    },
    cells: { style: { fontSize: "0.875rem" } },
  };

  return (
    <div className="space-y-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <motion.div
            key={stat.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: i * 0.07 }}
            className="bg-white/80 backdrop-blur-sm rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-all"
          >
            <div className="flex justify-between items-start">
              <div>
                <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{stat.name}</p>
                <h2 className="mt-1 text-3xl font-bold text-gray-800">{stat.value}</h2>
              </div>
              <div className={`p-2.5 rounded-xl ${stat.bg} ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Table Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className="relative bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-100 shadow-xl p-6 space-y-4"
      >
        <div className="absolute -right-20 -top-20 w-64 h-64 rounded-full bg-indigo-100/30 filter blur-3xl pointer-events-none" />
        <div className="absolute -left-10 -bottom-10 w-48 h-48 rounded-full bg-purple-100/20 filter blur-2xl pointer-events-none" />

        <div className="relative z-10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, type, platform…"
              value={searchText}
              onChange={handleSearchChange}
              className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white"
            />
          </div>
          <p className="text-sm text-gray-500">
            Showing <span className="font-semibold text-gray-700">{filteredData.length}</span> of <span className="font-semibold text-gray-700">{data.length}</span> properties
          </p>
        </div>

        <div className="relative z-10 overflow-x-auto rounded-xl border border-gray-100">
          <DataTable
            columns={columns}
            data={filteredData}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50]}
            highlightOnHover
            fixedHeader
            fixedHeaderScrollHeight="480px"
            customStyles={customStyles}
            progressPending={isLoading}
            progressComponent={
              <div className="py-10 flex flex-col items-center gap-3 text-gray-400">
                <div className="w-8 h-8 rounded-full border-4 border-indigo-300 border-t-transparent animate-spin" />
                <span className="text-sm">Loading properties…</span>
              </div>
            }
            noDataComponent={
              <div className="p-10 text-center text-gray-500">
                <div className="mx-auto w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mb-3">
                  <Search className="w-7 h-7 text-gray-400" />
                </div>
                <p className="font-medium">No properties found</p>
                <p className="text-xs mt-1 text-gray-400">Try adjusting your search</p>
              </div>
            }
          />
        </div>
      </motion.div>
    </div>
  );
}
